import React from 'react';
import { Collapse, Typography, Button, message } from 'antd';
import {
  ChromeOutlined,
  QuestionCircleOutlined,
  CopyOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { BrowserInfo, BrowserType } from '../hooks/useBrowserDetection';
import {
  CAMERA_HELP,
  MICROPHONE_HELP,
  SCREEN_SHARE_HELP,
  CAMERA_ISSUES,
  MICROPHONE_ISSUES,
  SCREEN_SHARE_ISSUES,
  CommonIssue,
  BrowserHelpItem,
} from '../constants/browserHelp';
import '../DeviceSetupWizard.scss';

type HelpType = 'camera' | 'microphone' | 'screen';

interface HelpAccordionsProps {
  type: HelpType;
  browserInfo: BrowserInfo;
}

const BROWSER_ICONS: Record<BrowserType, React.ReactNode> = {
  chrome: <ChromeOutlined />,
  firefox: <span>🦊</span>,
  edge: <span>🌐</span>,
  safari: <span>🧭</span>,
  opera: <span>🎭</span>,
  unknown: <QuestionCircleOutlined />,
};

const HelpAccordions: React.FC<HelpAccordionsProps> = ({ type, browserInfo }) => {
  const getHelpData = (): Record<BrowserType, BrowserHelpItem> => {
    switch (type) {
      case 'camera':
        return CAMERA_HELP;
      case 'microphone':
        return MICROPHONE_HELP;
      case 'screen':
        return SCREEN_SHARE_HELP;
      default:
        return CAMERA_HELP;
    }
  };

  const getIssuesData = (): CommonIssue[] => {
    switch (type) {
      case 'camera':
        return CAMERA_ISSUES;
      case 'microphone':
        return MICROPHONE_ISSUES;
      case 'screen':
        return SCREEN_SHARE_ISSUES;
      default:
        return [];
    }
  };

  const helpData = getHelpData();
  const issuesData = getIssuesData();
  const currentBrowser = browserInfo.browser;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  // Order browsers with current browser first
  const orderedBrowsers: BrowserType[] = [
    currentBrowser,
    ...(['chrome', 'firefox', 'edge', 'safari', 'opera'] as BrowserType[]).filter(
      (b) => b !== currentBrowser && b !== 'unknown'
    ),
  ];

  const renderBrowserHelp = (browser: BrowserType) => {
    const help = helpData[browser];
    if (!help) return null;

    const isCurrentBrowser = browser === currentBrowser;

    return (
      <Collapse.Panel
        key={browser}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            {BROWSER_ICONS[browser]}
            <span>How to enable in {help.displayName}</span>
            {isCurrentBrowser && (
              <span className="browser-indicator">Your browser</span>
            )}
          </div>
        }
        className={isCurrentBrowser ? 'highlighted' : ''}
      >
        <ol className="help-steps">
          {help.steps.map((step, index) => (
            <li key={index}>
              <span className="step-number">{index + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        {help.settingsUrl && (
          <div className="settings-url">
            <span>
              Settings URL: <code>{help.settingsUrl}</code>
            </span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(help.settingsUrl!)}
            >
              Copy
            </Button>
          </div>
        )}

        {help.notes && help.notes.length > 0 && (
          <ul className="help-notes">
            {help.notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        )}
      </Collapse.Panel>
    );
  };

  return (
    <div className="help-accordions">
      <div className="help-header">
        <QuestionCircleOutlined />
        Need Help?
      </div>

      <Collapse
        accordion
        bordered={false}
        defaultActiveKey={[currentBrowser]}
        expandIconPosition="end"
      >
        {orderedBrowsers.map((browser) => renderBrowserHelp(browser))}

        {/* Common Issues */}
        <Collapse.Panel
          key="common-issues"
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <QuestionCircleOutlined />
              <span>Common Issues & Fixes</span>
            </div>
          }
        >
          <div className="common-issues-content">
            {issuesData.map((issue, index) => (
              <div key={index} className="issue-item">
                <div className="issue-question">{issue.question}</div>
                <div className="issue-answer">{issue.answer}</div>
              </div>
            ))}
          </div>
        </Collapse.Panel>

        {/* Contact Support */}
        <Collapse.Panel
          key="support"
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CustomerServiceOutlined />
              <span>Still not working?</span>
            </div>
          }
        >
          <div style={{ padding: '8px 0' }}>
            <Typography.Paragraph>
              If you're still experiencing issues after trying the steps above:
            </Typography.Paragraph>
            <ul>
              <li>Try refreshing the page</li>
              <li>Try using a different browser (Chrome recommended)</li>
              <li>Check your device's system settings</li>
              <li>Restart your browser or computer</li>
            </ul>
            <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
              If problems persist, contact support with details about your browser
              ({browserInfo.browserName}) and operating system ({browserInfo.osName}).
            </Typography.Paragraph>
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default HelpAccordions;
